import React from 'react';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';

import { copilotDrawerWidth, copilotDrawerWidthStorageKey, normalizeCopilotDrawerWidth } from '../constants';
import type { CopilotDrawerTabValue } from '../drawer/CopilotDrawerHeader';

const copilotDrawerWidthChangeEventName = 'copilot-drawer-width-change';
const defaultCopilotDrawerTab: CopilotDrawerTabValue = 'chat';
const validCopilotDrawerTabs = new Set<CopilotDrawerTabValue>(['chat', 'sessions', 'tasks']);
let copilotDrawerActivityCount = 0;
const copilotDrawerActivityCountListeners = new Set<() => void>();
let copilotDrawerSessionId: string | null = null;
const copilotDrawerSessionIdListeners = new Set<() => void>();

const normalizeCopilotDrawerTab = (nextTab: string | null | undefined): CopilotDrawerTabValue => {
  if (validCopilotDrawerTabs.has(nextTab as CopilotDrawerTabValue)) {
    return nextTab as CopilotDrawerTabValue;
  }

  return defaultCopilotDrawerTab;
};

const getViewportWidth = () => {
  if (typeof window === 'undefined') {
    return copilotDrawerWidth;
  }

  return window.innerWidth;
};

const getStoredCopilotDrawerWidth = () => {
  if (typeof window === 'undefined') {
    return copilotDrawerWidth;
  }

  try {
    const storedWidth = Number(window.localStorage.getItem(copilotDrawerWidthStorageKey));
    return normalizeCopilotDrawerWidth(storedWidth, getViewportWidth());
  } catch {
    return normalizeCopilotDrawerWidth(Number.NaN, getViewportWidth());
  }
};

const broadcastCopilotDrawerWidthChange = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(copilotDrawerWidthChangeEventName));
};

const setStoredCopilotDrawerWidth = (nextWidth: number) => {
  if (typeof window === 'undefined') {
    return normalizeCopilotDrawerWidth(nextWidth, copilotDrawerWidth);
  }

  const normalizedWidth = normalizeCopilotDrawerWidth(nextWidth, getViewportWidth());

  try {
    window.localStorage.setItem(copilotDrawerWidthStorageKey, normalizedWidth.toString());
  } catch {
    // Ignore localStorage failures and keep the in-memory update path.
  }

  broadcastCopilotDrawerWidthChange();
  return normalizedWidth;
};

const subscribeCopilotDrawerActivityCount = (listener: () => void) => {
  copilotDrawerActivityCountListeners.add(listener);

  return () => {
    copilotDrawerActivityCountListeners.delete(listener);
  };
};

const getCopilotDrawerActivityCount = () => copilotDrawerActivityCount;

const subscribeCopilotDrawerSessionId = (listener: () => void) => {
  copilotDrawerSessionIdListeners.add(listener);

  return () => {
    copilotDrawerSessionIdListeners.delete(listener);
  };
};

const getCopilotDrawerSessionId = () => copilotDrawerSessionId;

const setSharedCopilotDrawerActivityCount = (nextCount: number) => {
  const normalizedCount = Math.max(0, nextCount || 0);

  if (normalizedCount === copilotDrawerActivityCount) {
    return normalizedCount;
  }

  copilotDrawerActivityCount = normalizedCount;
  copilotDrawerActivityCountListeners.forEach((listener) => listener());
  return normalizedCount;
};

const setSharedCopilotDrawerSessionId = (nextSessionId: string | null) => {
  const normalizedSessionId = nextSessionId || null;

  if (normalizedSessionId === copilotDrawerSessionId) {
    return normalizedSessionId;
  }

  copilotDrawerSessionId = normalizedSessionId;
  copilotDrawerSessionIdListeners.forEach((listener) => listener());
  return normalizedSessionId;
};

export const useCopilotDrawerState = () => {
  const [copilotDrawerOpen, setCopilotDrawerOpen] = useQueryParam('copilotDrawerOpen', BooleanParam);
  const [copilotDrawerTabUrlState, setCopilotDrawerTabUrlState] = useQueryParam('copilotDrawerTab', StringParam);
  const [drawerWidth, setDrawerWidth] = React.useState<number>(() => getStoredCopilotDrawerWidth());
  const sharedActivityCount = React.useSyncExternalStore(
    subscribeCopilotDrawerActivityCount,
    getCopilotDrawerActivityCount,
    () => 0
  );
  const sharedSessionId = React.useSyncExternalStore(
    subscribeCopilotDrawerSessionId,
    getCopilotDrawerSessionId,
    () => null
  );
  const copilotDrawerTab = normalizeCopilotDrawerTab(copilotDrawerTabUrlState);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncDrawerWidth = () => {
      setDrawerWidth(getStoredCopilotDrawerWidth());
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== copilotDrawerWidthStorageKey) {
        return;
      }

      syncDrawerWidth();
    };

    const handleResize = () => {
      setStoredCopilotDrawerWidth(getStoredCopilotDrawerWidth());
    };

    syncDrawerWidth();
    window.addEventListener(copilotDrawerWidthChangeEventName, syncDrawerWidth);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener(copilotDrawerWidthChangeEventName, syncDrawerWidth);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const setCopilotDrawerWidth = React.useCallback(
    (nextWidth: number | ((currentWidth: number) => number)) => {
      const resolvedWidth = typeof nextWidth === 'function' ? nextWidth(drawerWidth) : nextWidth;
      const normalizedWidth = setStoredCopilotDrawerWidth(resolvedWidth);
      setDrawerWidth(normalizedWidth);
    },
    [drawerWidth]
  );

  const setCopilotDrawerActivityCount = React.useCallback((nextCount: number) => {
    setSharedCopilotDrawerActivityCount(nextCount);
  }, []);

  const setCopilotDrawerSessionId = React.useCallback((nextSessionId: string | null) => {
    setSharedCopilotDrawerSessionId(nextSessionId);
  }, []);

  const setCopilotDrawerTab = React.useCallback(
    (nextTab: CopilotDrawerTabValue, updateType?: Parameters<typeof setCopilotDrawerTabUrlState>[1]) => {
      const normalizedTab = normalizeCopilotDrawerTab(nextTab);

      setCopilotDrawerTabUrlState(normalizedTab === defaultCopilotDrawerTab ? undefined : normalizedTab, updateType);
    },
    [setCopilotDrawerTabUrlState]
  );

  return {
    copilotDrawerOpen: Boolean(copilotDrawerOpen),
    copilotDrawerTab,
    copilotDrawerWidth: drawerWidth,
    copilotDrawerActivityCount: sharedActivityCount,
    copilotDrawerSessionId: sharedSessionId,
    setCopilotDrawerActivityCount,
    setCopilotDrawerTab,
    setCopilotDrawerSessionId,
    setCopilotDrawerWidth,
    setCopilotDrawerOpen,
  };
};
